'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import LoginModal from '@/components/auth/loginModal';
import { setPostLoginRedirect } from '@/lib/client/postLoginRedirect';
import { useAuth } from '@/providers/userContext';
import { useHeartInteractions } from '@/lib/client/useHeartInteractions';
import CheckIcon from 'assets/icons/check.svg';
import HeartPlusIcon from 'assets/icons/heart_plus.svg';
import { addPendingVisit, consumeCheckinClickTimestamp, setCheckinClickTimestamp } from '@/lib/client/pendingVisits';
const QrScannerModal = dynamic(() => import('./QrScannerModal'), {
  ssr: false
});
function formatShortDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit'
  });
}
function getQrRouteFromScan(rawValue) {
  if (!rawValue) return null;
  const raw = String(rawValue).trim();
  const match = raw.match(/\/qr\/([^/?#]+)/i);
  if (match?.[1]) return `/qr/${match[1]}`;
  try {
    const parsed = new URL(raw);
    const urlMatch = parsed.pathname.match(/\/qr\/([^/?#]+)/i);
    if (urlMatch?.[1]) return `/qr/${urlMatch[1]}`;
  } catch {
    // ignore non-URL values
  }
  if (/^[a-z0-9-]+$/i.test(raw)) return `/qr/${raw}`;
  return null;
}
export default function QrCheckinAction(t0) {
  const $ = _c(73);
  const {
    icon,
    label: t1,
    actionClass: t2,
    variant: t3,
    heartId
  } = t0;
  const label = t1 === undefined ? "Check in Now" : t1;
  const actionClass = t2 === undefined ? "" : t2;
  const variant = t3 === undefined ? "grid" : t3;
  const {
    user,
    authChecked
  } = useAuth();
  const {
    visited,
    visitTimestamp,
    markVisited
  } = useHeartInteractions(heartId);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerKey, setScannerKey] = useState(0);
  const [scanError, setScanError] = useState("");
  const processedCheckinRef = useRef(false);
  let t4;
  if ($[0] !== searchParams) {
    t4 = searchParams?.get("checkin");
    $[0] = searchParams;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const isCheckin = t4 === "true";
  let t5;
  let t6;
  if ($[2] !== isCheckin) {
    t5 = () => {
      if (!isCheckin) {
        processedCheckinRef.current = false;
      }
    };
    t6 = [isCheckin];
    $[2] = isCheckin;
    $[3] = t5;
    $[4] = t6;
  } else {
    t5 = $[3];
    t6 = $[4];
  }
  useEffect(t5, t6);
  let t7;
  if ($[5] !== visitTimestamp) {
    t7 = formatShortDate(visitTimestamp);
    $[5] = visitTimestamp;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const date = t7;
  const visitedLabel = date ? `Visited ${date}` : "Visited";
  let t8;
  if ($[7] !== pathname || $[8] !== router || $[9] !== searchParams) {
    t8 = () => {
      const params = new URLSearchParams(searchParams?.toString());
      params.delete("checkin");
      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : pathname);
    };
    $[7] = pathname;
    $[8] = router;
    $[9] = searchParams;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const clearCheckinParam = t8;
  let t10;
  let t9;
  if ($[11] !== authChecked || $[12] !== clearCheckinParam || $[13] !== heartId || $[14] !== isCheckin || $[15] !== markVisited || $[16] !== user) {
    t9 = () => {
      if (!isCheckin || !authChecked || processedCheckinRef.current) {
        return;
      }
      processedCheckinRef.current = true;
      const timestamp = consumeCheckinClickTimestamp(heartId) || new Date().toISOString();
      if (user) {
        markVisited(timestamp);
      } else {
        addPendingVisit(heartId, timestamp);
      }
      clearCheckinParam();
    };
    t10 = [authChecked, clearCheckinParam, heartId, isCheckin, markVisited, user];
    $[11] = authChecked;
    $[12] = clearCheckinParam;
    $[13] = heartId;
    $[14] = isCheckin;
    $[15] = markVisited;
    $[16] = user;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  useEffect(t9, t10);
  let t11;
  if ($[19] !== heartId || $[20] !== pathname || $[21] !== searchParams || $[22] !== user || $[23] !== visited) {
    t11 = () => {
      if (visited) {
        return;
      }
      if (!user) {
        const params_0 = searchParams?.toString();
        const target = params_0 ? `${pathname}?${params_0}` : pathname;
        setPostLoginRedirect(target);
        setShowLoginModal(true);
        return;
      }
      setCheckinClickTimestamp(heartId, new Date().toISOString());
      setScanError("");
      setScannerKey(_temp);
      setShowScanner(true);
    };
    $[19] = heartId;
    $[20] = pathname;
    $[21] = searchParams;
    $[22] = user;
    $[23] = visited;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const handleClick = t11;
  let t12;
  if ($[25] !== router) {
    t12 = value => {
      const route = getQrRouteFromScan(value);
      if (!route) {
        setScanError("We could not read that QR code. Please try again.");
        return;
      }
      setShowScanner(false);
      router.push(route);
    };
    $[25] = router;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const handleScan = t12;
  let t13;
  if ($[27] !== icon) {
    t13 = icon ?? <Image src={HeartPlusIcon} alt="" width={24} height={24} aria-hidden={true} />;
    $[27] = icon;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const defaultIcon = t13;
  let t14;
  if ($[29] !== defaultIcon || $[30] !== visited) {
    t14 = visited ? <Image src={CheckIcon} alt="" width={24} height={24} aria-hidden={true} className="filter brightness-0 invert" /> : defaultIcon;
    $[29] = defaultIcon;
    $[30] = visited;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const iconNode = t14;
  const labelText = visited ? visitedLabel : label;
  if (variant === "pill") {
    let t15;
    if ($[32] !== iconNode) {
      t15 = <span className="flex h-11 w-11 items-center justify-center rounded-full">{iconNode}</span>;
      $[32] = iconNode;
      $[33] = t15;
    } else {
      t15 = $[33];
    }
    let t16;
    if ($[34] !== labelText) {
      t16 = <span className="leading-tight">{labelText}</span>;
      $[34] = labelText;
      $[35] = t16;
    } else {
      t16 = $[35];
    }
    let t17;
    if ($[36] !== handleClick || $[37] !== t15 || $[38] !== t16 || $[39] !== visited) {
      t17 = <button type="button" onClick={handleClick} aria-disabled={visited} disabled={visited} className="flex flex-col items-center gap-2 rounded-full bg-white/10 px-3 py-3 text-white backdrop-blur transition hover:bg-white/20">{t15}{t16}</button>;
      $[36] = handleClick;
      $[37] = t15;
      $[38] = t16;
      $[39] = visited;
      $[40] = t17;
    } else {
      t17 = $[40];
    }
    let t18;
    if ($[41] !== showLoginModal) {
      t18 = showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />;
      $[41] = showLoginModal;
      $[42] = t18;
    } else {
      t18 = $[42];
    }
    let t19;
    if ($[43] !== handleScan || $[44] !== scanError || $[45] !== scannerKey || $[46] !== showScanner) {
      t19 = showScanner && <QrScannerModal key={scannerKey} isOpen={showScanner} onClose={() => {
        setShowScanner(false);
        setScanError("");
      }} onScan={handleScan} errorMessage={scanError} />;
      $[43] = handleScan;
      $[44] = scanError;
      $[45] = scannerKey;
      $[46] = showScanner;
      $[47] = t19;
    } else {
      t19 = $[47];
    }
    let t20;
    if ($[48] !== t17 || $[49] !== t18 || $[50] !== t19) {
      t20 = <>{t17}{t18}{t19}</>;
      $[48] = t17;
      $[49] = t18;
      $[50] = t19;
      $[51] = t20;
    } else {
      t20 = $[51];
    }
    return t20;
  }
  const t15 = `flex flex-col items-center gap-3 lg:px-3 px-1 lg:pb-3 text-center ${actionClass}`;
  visited ? "bg-[var(--theme-color)]" : "bg-[var(--theme-color)]";
  let t16;
  if ($[52] !== iconNode) {
    t16 = <span className={"flex h-12 w-12 items-center justify-center rounded-full text-white \n            bg-[var(--theme-color)]\n            "}>{iconNode}</span>;
    $[52] = iconNode;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] !== labelText) {
    t17 = <span className="lg:text-[#323232] max-w-24 text-white leading-[1.12em] lg:text-base text-[0.65em] font-semibold">{labelText}</span>;
    $[54] = labelText;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== handleClick || $[57] !== t15 || $[58] !== t16 || $[59] !== t17 || $[60] !== visited) {
    t18 = <button type="button" onClick={handleClick} aria-disabled={visited} disabled={visited} className={t15}>{t16}{t17}</button>;
    $[56] = handleClick;
    $[57] = t15;
    $[58] = t16;
    $[59] = t17;
    $[60] = visited;
    $[61] = t18;
  } else {
    t18 = $[61];
  }
  let t19;
  if ($[62] !== showLoginModal) {
    t19 = showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />;
    $[62] = showLoginModal;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  let t20;
  if ($[64] !== handleScan || $[65] !== scanError || $[66] !== scannerKey || $[67] !== showScanner) {
    t20 = showScanner && <QrScannerModal key={scannerKey} isOpen={showScanner} onClose={() => {
      setShowScanner(false);
      setScanError("");
    }} onScan={handleScan} errorMessage={scanError} />;
    $[64] = handleScan;
    $[65] = scanError;
    $[66] = scannerKey;
    $[67] = showScanner;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  let t21;
  if ($[69] !== t18 || $[70] !== t19 || $[71] !== t20) {
    t21 = <>{t18}{t19}{t20}</>;
    $[69] = t18;
    $[70] = t19;
    $[71] = t20;
    $[72] = t21;
  } else {
    t21 = $[72];
  }
  return t21;
}
function _temp(key) {
  return key + 1;
}